/**
 * The plain words the Catalog shows for a template (#1727): a name and a one-line description, never a slug. The
 * full text stays reachable (as the element's title, or in the task's Details sheet), so cutting it to a line hides
 * nothing for good.
 */

/** The business functions the catalog filters by, as the API names them, with the words the screen shows. */
export const BUSINESS_FUNCTION_OPTIONS = [
    { value: 'support', label: 'Support' },
    { value: 'marketing', label: 'Marketing' },
    { value: 'sales', label: 'Sales' },
    { value: 'operations', label: 'Operations' },
    { value: 'engineering', label: 'Engineering' },
] as const

/** The filter value that stands for no business function filter at all. */
export const ALL_FUNCTIONS = 'all'

/**
 * The query the two catalog filters add to a list request: `businessFunction` unless "All functions" is chosen, and
 * `needsNoTools=true` only while the box is ticked. A cleared filter sends nothing, so the server applies none.
 */
export function catalogFilterQuery(businessFunction: string, needsNoTools: boolean): Record<string, string> {
    const query: Record<string, string> = {}
    if (businessFunction && businessFunction !== ALL_FUNCTIONS) query['businessFunction'] = businessFunction
    if (needsNoTools) query['needsNoTools'] = 'true'
    return query
}

/** The task's name as the Catalog shows it. An entry with no name reads as unnamed, never as its slug. */
export function taskDisplayName(task: { name?: string | null }): string {
    return task.name?.trim() || 'Unnamed task'
}

/**
 * The first sentence of `text`, with its line breaks read as spaces: up to and including the first `.`, `!` or `?`
 * that ends a word. Text with no such stop is all one sentence.
 */
export function firstSentence(text: string | null | undefined): string {
    const flat = (text ?? '').replace(/\s+/g, ' ').trim()
    const match = /^.*?[.!?](?=\s|$)/.exec(flat)
    return match ? match[0] : flat
}

type CardTask = { name?: string | null; origin?: string; status?: string }

/** The tasks a clone of a role or team takes: OS-origin and deprecated ones are never cloned. */
export function clonableTasks<T extends CardTask>(tasks: readonly T[] | null | undefined): T[] {
    return (tasks ?? []).filter(task => task.origin !== 'os' && task.status !== 'deprecated')
}

/** A role card's one line, built from what it does: "2 tasks: Answer tickets, Escalate", or "No tasks yet". */
export function roleLine(role: { tasks?: readonly CardTask[] | null }): string {
    const tasks = clonableTasks(role.tasks)
    if (tasks.length === 0) return 'No tasks yet'
    const count = `${tasks.length} task${tasks.length === 1 ? '' : 's'}`
    return `${count}: ${tasks.map(taskDisplayName).join(', ')}`
}

/** What the clone review step says for a task that declares no per-run spend limit (#1728): what is missing, and why. */
export const NO_COST_ESTIMATE = 'No cost estimate: this task sets no per-run spend limit.'

/**
 * A Catalog task's expected cost per run, as the clone review step shows it (#1728): its declared per-run spend limit,
 * which the OS stops a run at, worded as the cost ("Expected cost per run: up to $0.50"). US dollars to the cent; a
 * nonzero limit under a cent reads "under $0.01", never a `$0.00` that looks free, while a declared 0 is a real limit
 * and reads `$0.00`. A missing or null limit has no estimate, and says so.
 */
export function expectedCostPerRun(spend: { moneyUsdPerRun?: number | null } | null | undefined): string {
    const limit = spend?.moneyUsdPerRun
    if (typeof limit !== 'number' || !Number.isFinite(limit)) return NO_COST_ESTIMATE
    const amount = limit > 0 && limit < 0.01 ? 'under $0.01' : `$${limit.toFixed(2)}`
    return `Expected cost per run: up to ${amount}`
}

/** A team card's one line: its description, or its charter's purpose when it has none. */
export function teamLine(team: { description?: string | null; charter?: { purpose?: string | null } | null }): string {
    return team.description?.trim() ? (team.description ?? '') : (team.charter?.purpose ?? '')
}
